import { z } from "zod/v3";

/* ════════════════════════════════════════════════════════════════════════════
   SOCIAL MEDIA GROWTH CO-PILOT — Schemas, Prompts & Pipeline Steps
   ════════════════════════════════════════════════════════════════════════════ */

/* ── Social Brand Input ── */

export const SocialBrandInputSchema = z.object({
  // Base brand fields
  brandName: z.string().min(1),
  industry: z.string().min(1),
  website: z.string().url().optional(),
  competitors: z.array(z.string()).optional(),
  productDescription: z.string().optional(),

  // Social-specific fields
  platforms: z
    .array(z.enum(["youtube", "tiktok", "instagram", "x", "linkedin"]))
    .min(1)
    .max(5),
  socialGoal: z.enum([
    "brand_awareness",
    "lead_generation",
    "community_building",
    "product_launches",
    "thought_leadership",
  ]),
  targetAudience: z.string().optional(),
  contentStyle: z
    .enum(["educational", "entertaining", "behind_the_scenes", "ugc_style", "cinematic", "mixed"])
    .optional(),
  existingSocialHandles: z.record(z.string()).optional(),
  weeklyPostingCapacity: z.number().min(1).max(30).optional(),
  referenceCreators: z.array(z.string()).optional(),
});
export type SocialBrandInput = z.infer<typeof SocialBrandInputSchema>;

/* ── Social Query Plan (LLM-generated) ── */

export const SocialQueryPlanSchema = z.object({
  youtube_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum([
          "competitor_content",
          "trending_formats",
          "audience_engagement",
          "niche_discovery",
        ]),
        max_results: z.number().min(5).max(50),
        sort_by: z.enum(["relevance", "viewCount", "date"]).optional(),
      }),
    )
    .min(3)
    .max(10),

  tiktok_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum(["trending_sounds", "viral_formats", "competitor_content", "niche_trends"]),
        max_results: z.number().min(10).max(50),
      }),
    )
    .min(2)
    .max(8),

  apify_video_targets: z
    .array(
      z.object({
        url: z.string(),
        purpose: z.enum(["transcript_extraction", "content_analysis", "engagement_audit"]),
      }),
    )
    .min(0)
    .max(15),

  instagram_queries: z
    .array(
      z.object({
        username: z.string(),
        purpose: z.enum(["competitor_content", "content_analysis", "engagement_audit"]),
        max_results: z.number().min(5).max(30),
      }),
    )
    .min(0)
    .max(6),

  xai_web_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum([
          "viral_mechanics",
          "platform_algorithm",
          "content_trends",
          "industry_social",
        ]),
        allowed_domains: z.array(z.string()).optional(),
      }),
    )
    .min(2)
    .max(6),

  xai_x_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum([
          "viral_tweets",
          "influencer_content",
          "trending_topics",
          "competitor_engagement",
        ]),
        allowed_handles: z.array(z.string()).optional(),
        date_range: z.enum(["7d", "30d", "90d"]),
      }),
    )
    .min(2)
    .max(8),

  perplexity_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum(["platform_trends", "content_strategy_research", "audience_behavior"]),
        recency_filter: z.enum(["day", "week", "month"]),
      }),
    )
    .min(1)
    .max(5),

  google_search_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum(["trend_news", "algorithm_shifts", "platform_updates", "competitor_news"]),
      }),
    )
    .min(0)
    .max(5)
    .optional(),

  social_context: z.object({
    platform_priorities: z.array(z.string()),
    content_themes: z.array(z.string()),
    competitor_handles: z.record(z.array(z.string())),
    trending_hashtags: z.array(z.string()),
  }),
});
export type SocialQueryPlan = z.infer<typeof SocialQueryPlanSchema>;

/* ── Collection Result (social-specific source types) ── */

export interface SocialCollectionResult {
  source:
    | "youtube_search"
    | "youtube_transcript"
    | "tiktok_hashtag"
    | "tiktok_profile"
    | "instagram_profile"
    | "xai_web"
    | "xai_x"
    | "perplexity_research"
    | "google_search";
  query: string;
  purpose: string;
  raw_data: string;
  videos_found?: number;
  citations: string[];
  timestamp: string;
  latency_ms: number;
  error?: string;
}

/* ── Video Entry (from any platform) ── */

export interface VideoEntry {
  platform: "youtube" | "tiktok" | "instagram";
  id: string;
  url: string;
  title: string;
  description: string;
  author: string;
  views: number;
  likes: number;
  comments: number;
  shares?: number;
  duration?: number;
  published_at: string;
  hashtags?: string[];
  sound?: { name: string; author: string };
  transcript?: string;
}

/* ── Transcript Entry ── */

export interface TranscriptEntry {
  video_id: string;
  platform: "youtube" | "tiktok";
  url: string;
  title: string;
  transcript: string;
  duration_seconds?: number;
}

/* ── Engagement Benchmarks ── */

export interface EngagementBenchmark {
  platform: string;
  avg_views: number;
  avg_likes: number;
  avg_comments: number;
  avg_engagement_rate: number;
  top_performing_format: string;
  sample_size: number;
}

/* ── Social Collection Report ── */

export interface SocialCollectionReport {
  brand_input: SocialBrandInput;
  query_plan: SocialQueryPlan;
  results: SocialCollectionResult[];
  errors: Array<{ source: string; query: string; error: string }>;
  stats: { total: number; successful: number; failed: number; duration_ms: number };
  video_library: VideoEntry[];
  transcript_library: TranscriptEntry[];
  engagement_benchmarks: Record<string, EngagementBenchmark>;
}

/* ── Distilled Intelligence (reuse structure from coAgentPrompts) ── */

export { SectionDistillationSchema } from "./coAgentPrompts";
export type { SectionDistillation, DistilledIntelligence } from "./coAgentPrompts";

/* ── Trend Report Schema ── */

export const TrendReportSchema = z.object({
  platform_trends: z
    .array(
      z.object({
        platform: z.string(),
        trend_name: z.string(),
        description: z.string(),
        virality_score: z.number().min(0).max(100),
        relevance_to_brand: z.enum(["high", "medium", "low"]),
        example_videos: z
          .array(
            z.object({
              title: z.string(),
              url: z.string(),
              views: z.number(),
              why_it_works: z.string(),
            }),
          )
          .max(3),
        recommended_adaptation: z.string(),
      }),
    )
    .min(3)
    .max(15),

  trending_sounds_and_hooks: z
    .array(
      z.object({
        type: z.enum(["sound", "hook", "transition", "format"]),
        name: z.string(),
        platform: z.string(),
        usage_count: z.string(),
        brand_fit: z.enum(["perfect", "adaptable", "stretch"]),
        adaptation_idea: z.string(),
      }),
    )
    .max(10),

  content_velocity: z.object({
    industry_avg_posts_per_week: z.record(z.number()),
    top_performer_frequency: z.record(z.number()),
    recommended_frequency: z.record(z.number()),
  }),
});
export type TrendReport = z.infer<typeof TrendReportSchema>;

/* ── Competitor Content Audit Schema ── */

export const CompetitorAuditSchema = z.object({
  competitors: z
    .array(
      z.object({
        name: z.string(),
        platforms: z.record(
          z.object({
            handle: z.string(),
            followers: z.string(),
            avg_views: z.string(),
            avg_engagement_rate: z.string(),
            posting_frequency: z.string(),
            top_content: z
              .array(
                z.object({
                  title: z.string(),
                  url: z.string().optional(),
                  views: z.string(),
                  format: z.string(),
                  hook: z.string(),
                  why_it_worked: z.string(),
                }),
              )
              .max(3),
            content_themes: z.array(z.string()),
            weaknesses: z.array(z.string()),
          }),
        ),
        overall_strategy: z.string(),
      }),
    )
    .min(2)
    .max(5),

  gap_analysis: z
    .array(
      z.object({
        gap: z.string(),
        opportunity: z.string(),
        difficulty: z.enum(["easy", "medium", "hard"]),
        potential_impact: z.enum(["high", "medium", "low"]),
      }),
    )
    .max(8),
});
export type CompetitorAudit = z.infer<typeof CompetitorAuditSchema>;

/* ── Template Library Schema ── */

export const TemplateLibrarySchema = z.object({
  templates: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        format: z.enum([
          "talking_head",
          "screen_recording",
          "b_roll_montage",
          "before_after",
          "listicle",
          "day_in_the_life",
          "tutorial",
          "reaction",
          "duet_stitch",
          "product_demo",
          "testimonial",
          "behind_the_scenes",
        ]),
        platforms: z.array(z.string()),
        duration: z.string(),
        structure: z.array(
          z.object({
            timestamp: z.string(),
            element: z.string(),
            visual: z.string(),
            audio: z.string(),
          }),
        ),
        why_it_works: z.string(),
        example_references: z.array(z.string()),
        brand_adaptation: z.string(),
      }),
    )
    .min(5)
    .max(12),
});
export type TemplateLibrary = z.infer<typeof TemplateLibrarySchema>;

/* ── Social Calendar Schema ── */

export const SocialCalendarSchema = z.object({
  strategy_summary: z.string(),
  weekly_rhythm: z.record(
    z.object({
      posts_per_week: z.number(),
      best_posting_times: z.array(z.string()),
      content_mix: z.record(z.number()),
    }),
  ),

  four_week_calendar: z.array(
    z.object({
      week: z.number(),
      theme: z.string(),
      posts: z.array(
        z.object({
          day: z.string(),
          platform: z.string(),
          template_id: z.string(),
          title: z.string(),
          hook: z.string(),
          content_pillar: z.string(),
          hashtags: z.array(z.string()),
          estimated_reach: z.string(),
          production_effort: z.enum(["low", "medium", "high"]),
          cross_post_to: z.array(z.string()).optional(),
        }),
      ),
    }),
  ),

  kpis: z
    .array(
      z.object({
        metric: z.string(),
        target_30_day: z.string(),
        target_90_day: z.string(),
      }),
    )
    .max(8),
});
export type SocialCalendar = z.infer<typeof SocialCalendarSchema>;

/* ── Video Brief Batch Schema ── */

export const VideoBriefBatchSchema = z.object({
  briefs: z
    .array(
      z.object({
        calendar_slot: z.string(),
        template_id: z.string(),
        title: z.string(),
        platform: z.string(),
        duration: z.string(),
        script: z.object({
          hook: z.string(),
          body: z.array(
            z.object({
              timestamp: z.string(),
              visual: z.string(),
              audio: z.string(),
              text_overlay: z.string().optional(),
            }),
          ),
          cta: z.string(),
        }),
        production_notes: z.object({
          filming_style: z.string(),
          equipment_needed: z.string(),
          estimated_production_time: z.string(),
          can_automate: z.boolean(),
          automation_pipeline: z.enum(["cinematic", "ugc", "manual"]).optional(),
        }),
        trending_elements: z.array(z.string()),
      }),
    )
    .min(4)
    .max(12),
});
export type VideoBriefBatch = z.infer<typeof VideoBriefBatchSchema>;

/* ── Pipeline Steps ── */

export const SOCIAL_PIPELINE_STEPS = [
  { label: "Brand + Social Input", icon: "settings", service: "User Input" },
  { label: "Social Data Collection", icon: "search", service: "Multi-Engine" },
  { label: "Trend Analysis", icon: "trending-up", service: "Gemini Pro" },
  { label: "Competitor Audit", icon: "users", service: "Gemini Pro" },
  { label: "Template Library", icon: "layout", service: "Gemini Pro" },
  { label: "Social Calendar", icon: "calendar", service: "Gemini Pro" },
  { label: "Video Briefs", icon: "video", service: "Gemini Pro" },
  { label: "Summary", icon: "zap", service: "System" },
];

/* ── Progress Totals Helper ── */

export function getSocialCollectionProgressTotals(plan: SocialQueryPlan): {
  total: number;
  perTrack: Record<string, number>;
} {
  const perTrack: Record<string, number> = {
    youtube_search: plan.youtube_queries.length,
    youtube_transcript: plan.apify_video_targets.filter(
      (t) => t.purpose === "transcript_extraction",
    ).length,
    tiktok_hashtag: plan.tiktok_queries.length,
    instagram_profile: plan.instagram_queries.length,
    xai_web: plan.xai_web_queries.length,
    xai_x: plan.xai_x_queries.length,
    perplexity_research: plan.perplexity_queries.length,
    google_search: plan.google_search_queries?.length ?? 0,
  };

  return {
    perTrack,
    total: Object.values(perTrack).reduce((sum, v) => sum + v, 0),
  };
}
